import { Fragment, useEffect, useMemo, useState } from "react";
import { getCustomerHealth } from "../../distflowAPI/salesAnalyticsApi";
import "./NeedsAttention.css";

const PAGE = 25;
const currency = new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 });
const exactCurrency = new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" });
const money = (value) => currency.format(Number(value || 0));
const date = (value, year = false) => value ? new Intl.DateTimeFormat("en-US", { month: "short", day: "numeric", ...(year ? { year: "numeric" } : {}), timeZone: "UTC" }).format(new Date(`${value}T00:00:00Z`)) : "—";
const STATUS_ORDER = ["late", "switching", "spending_less", "ordering_less", "smaller_orders"];
const STATUS_LABELS = {
  late: "Late", switching: "Buying elsewhere?", spending_less: "Spending less",
  ordering_less: "Ordering less often", smaller_orders: "Smaller orders",
};
// Only these drill-downs apply per customer; item-level ones (route, vendor, ...) do not.
const CUSTOMER_FILTERS = new Set(["customer", "account", "account_type", "salesman"]);

function cadence(days) {
  if (days <= 1) return "daily";
  if (days === 7) return "weekly";
  if (days === 14) return "every 2 weeks";
  return `every ${days} days`;
}

function FollowUp({ followUp }) {
  if (followUp?.open_tasks) return <span className="needs-attention-followup needs-attention-followup--open">{followUp.open_tasks} open {followUp.open_tasks === 1 ? "task" : "tasks"}</span>;
  if (followUp?.last_task) return <span className="needs-attention-followup">Last task {date(followUp.last_task.date)}{followUp.last_task.done ? " ✓" : ""}</span>;
  return <span className="needs-attention-followup needs-attention-followup--none">None</span>;
}

function Dropped({ title, items }) {
  if (!items?.length) return null;
  return <div className="needs-attention-dropped">
    <strong>{title}</strong>
    <ul>{items.map((item) => <li key={item.id}>
      <span>{item.name}{item.sku && <small> · {item.sku}</small>}</span>
      <span className={`needs-attention-drop needs-attention-drop--${item.status}`}>{item.status === "stopped" ? "Stopped" : "Cut back"}</span>
      <span>was {exactCurrency.format(Number(item.baseline_weekly))}/wk, now {exactCurrency.format(Number(item.recent_weekly))}/wk</span>
      <span>last bought {date(item.last_bought, true)}</span>
    </li>)}</ul>
  </div>;
}

export default function NeedsAttention({ asOf, territoryId, filters = [], onSelectCustomer }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [status, setStatus] = useState("all");
  const [query, setQuery] = useState("");
  const [hideFollowed, setHideFollowed] = useState(false);
  const [expanded, setExpanded] = useState(null);
  const [visible, setVisible] = useState(PAGE);
  const customerFilters = filters.filter((key) => CUSTOMER_FILTERS.has(String(key).split(":")[0]));
  const ignoredFilters = filters.length - customerFilters.length;
  const filterSignature = customerFilters.join("|");

  useEffect(() => {
    if (!asOf) return undefined;
    const controller = new AbortController();
    setLoading(true);
    setError("");
    Promise.resolve()
      .then(() => getCustomerHealth({ as_of: asOf, territory_id: territoryId, filters: filterSignature ? filterSignature.split("|") : [] }, { signal: controller.signal }))
      .then((result) => { if (!controller.signal.aborted) { setData(result || null); setVisible(PAGE); setExpanded(null); } })
      .catch((requestError) => {
        if (controller.signal.aborted || requestError?.code === "ERR_CANCELED") return;
        setData(null);
        setError("The needs-attention list could not be loaded.");
      })
      .finally(() => { if (!controller.signal.aborted) setLoading(false); });
    return () => controller.abort();
  }, [asOf, territoryId, filterSignature]);

  const rows = useMemo(() => {
    const term = query.trim().toLocaleLowerCase();
    return (data?.rows || []).filter((row) =>
      (status === "all" || row.status === status || (status === "switching" && (row.dropped_categories?.length || row.dropped_products?.length))) &&
      (!hideFollowed || !row.follow_up?.open_tasks) &&
      (!term || `${row.name} ${row.salesman?.name || ""}`.toLocaleLowerCase().includes(term))
    );
  }, [data, status, query, hideFollowed]);

  if (!data && !loading && !error) return null;
  const summary = data?.summary;
  const counts = summary?.by_status || {};
  const statusCount = (key) => key === "switching" ? summary?.switching ?? counts.switching : counts[key];

  return <section className="needs-attention" aria-label="Needs attention" aria-busy={loading}>
    <div className="sales-analytics-section-head">
      <div><span className="sales-analytics-eyebrow">Who to call this week</span><h2>Needs attention</h2></div>
      {data && <span className="sales-analytics-section-aside">Each customer compared with their own previous 12 weeks · last 4 weeks to {date(data.as_of, true)}</span>}
    </div>
    {error && <p className="needs-attention-error" role="alert">{error}</p>}
    {loading && !data && <p className="needs-attention-empty">Checking customers…</p>}
    {data && <div className={`needs-attention-card${loading ? " needs-attention-card--refreshing" : ""}`}>
      <div className="needs-attention-top">
        <div className="needs-attention-headline">
          <strong>{money(summary.at_risk_weekly)}<small>/week at risk</small></strong>
          <span>{summary.flagged} of {summary.regular_customers} regular customers need attention</span>
        </div>
        <div className="needs-attention-statuses" role="group" aria-label="Filter by reason">
          <button type="button" aria-pressed={status === "all"} className={status === "all" ? "is-selected" : ""} onClick={() => { setStatus("all"); setVisible(PAGE); }}>All <b>{summary.flagged}</b></button>
          {STATUS_ORDER.filter((key) => statusCount(key)).map((key) => <button type="button" key={key} aria-pressed={status === key}
            className={`needs-attention-status--${key}${status === key ? " is-selected" : ""}`} onClick={() => { setStatus(key); setVisible(PAGE); }}>
            {STATUS_LABELS[key]} <b>{statusCount(key)}</b>
          </button>)}
        </div>
      </div>
      <div className="needs-attention-tools">
        <input type="search" aria-label="Search customers or salesmen" placeholder="Search customers or salesmen…" value={query} onChange={(event) => { setQuery(event.target.value); setVisible(PAGE); }} />
        <label><input type="checkbox" checked={hideFollowed} onChange={(event) => setHideFollowed(event.target.checked)} /> Hide customers with an open task</label>
        {ignoredFilters > 0 && <span className="needs-attention-note">Route, vendor, category and product focus don’t apply to this list.</span>}
      </div>
      {rows.length ? <div className="needs-attention-table-wrap"><table aria-label="Customers needing attention">
        <thead><tr>
          <th scope="col">Customer</th><th scope="col">Why</th><th scope="col">Usually</th><th scope="col">Last 4 weeks</th>
          <th scope="col">At risk / week</th><th scope="col">Last order</th><th scope="col">Follow-up</th><th scope="col"><span className="needs-attention-hidden">Details</span></th>
        </tr></thead>
        <tbody>{rows.slice(0, visible).map((row) => {
          const open = expanded === row.key;
          const main = row.reasons.find((reason) => reason.type === row.status) || row.reasons[0];
          return <Fragment key={row.key}>
            <tr className={open ? "is-open" : ""}>
              <th scope="row">
                <button type="button" className="needs-attention-name" title={`Focus the page on ${row.name}`} onClick={() => onSelectCustomer?.(row)}>{row.name}</button>
                <small>{row.salesman?.name}</small>
              </th>
              <td className="needs-attention-why"><span className={`needs-attention-badge needs-attention-badge--${row.status}`}>{row.status_label}</span><small>{main?.detail}</small></td>
              <td>{money(row.baseline_weekly)}/wk<small>{cadence(row.typical_gap_days)}</small></td>
              <td>{money(row.recent_weekly)}/wk<small>{row.recent_orders} {row.recent_orders === 1 ? "order" : "orders"}</small></td>
              <td className="needs-attention-risk">{money(row.at_risk_weekly)}</td>
              <td>{date(row.last_order_date)}<small>{row.days_since_last_order} {row.days_since_last_order === 1 ? "day" : "days"} ago</small></td>
              <td><FollowUp followUp={row.follow_up} /></td>
              <td><button type="button" className="needs-attention-expand" aria-expanded={open} aria-label={`${open ? "Hide" : "Show"} details for ${row.name}`} onClick={() => setExpanded(open ? null : row.key)}>{open ? "−" : "+"}</button></td>
            </tr>
            {open && <tr className="needs-attention-details"><td colSpan={8}>
              <div className="needs-attention-details-grid">
                <div><strong>Signals</strong><ul>{row.reasons.map((reason) => <li key={reason.type}><b>{reason.label}:</b> {reason.detail}</li>)}</ul>
                  <p>Usually orders {cadence(row.typical_gap_days)}, so the next order was due {date(row.expected_by, true)}. Average order {exactCurrency.format(Number(row.baseline_avg_order))}{row.recent_avg_order != null && `, lately ${exactCurrency.format(Number(row.recent_avg_order))}`}.</p></div>
                <Dropped title="Categories they stopped or cut back" items={row.dropped_categories} />
                <Dropped title="Regular products they stopped" items={row.dropped_products} />
                {row.follow_up?.last_task && <div><strong>Latest task</strong><p>{date(row.follow_up.last_task.date, true)}{row.follow_up.last_task.by && ` · ${row.follow_up.last_task.by}`}{row.follow_up.last_task.done ? " · done" : " · open"}</p><p className="needs-attention-task">{row.follow_up.last_task.content || "No note"}</p></div>}
              </div>
            </td></tr>}
          </Fragment>;
        })}</tbody>
      </table></div> : <p className="needs-attention-empty">{data.rows.length ? "No customers match these filters." : "No regular customers are behind their usual pattern."}</p>}
      {rows.length > visible && <button type="button" className="needs-attention-more" onClick={() => setVisible(visible + PAGE)}>Show {Math.min(PAGE, rows.length - visible)} more of {rows.length - visible}</button>}
      {data.total > data.rows.length && <p className="needs-attention-note">Showing the {data.rows.length} customers with the most at risk out of {data.total}.</p>}
    </div>}
  </section>;
}
